import axios from 'axios';
import {getLocation, push} from 'connected-react-router';
import PropTypes from 'prop-types';
import querystring from 'query-string';
import React, {Component} from 'react';
import {connect} from 'react-redux';
import {mutate} from 'swr';
import {PreProcessorListUrl} from '../../../api/preprocessor/PreProcessorList';
import {toast} from '../../../base-components/StudioToast';
import URL, {ISP_OP, ISP_SUB_OP} from '../../../config/url';
import {projectFetch} from '../../../store/project';
import {addListener, removeListener} from '../../../store/statusChecker';
import {viewDisableTermination, viewEnableTermination} from '../../../store/view';
import Util from '../../../util';
import AssistantPanel from '../../DialogLegacy/AssistantPanel';
import {StageAssistantForm} from '../../StageAssistantForm';
import StageAnimation from '../StageAnimation/StageAnimation';

const PROGRESS_TYPE = 'IMPORT_ISP_FLOW';
class ISPUploadAction extends Component {
  constructor(props) {
    super(props);

    this.stageAnimation = new StageAnimation();
    this.requestImportFinalization = this.requestImportFinalization.bind(this);
    this.displayISPUploadProgress = this.displayISPUploadProgress.bind(this);
    this.terminateISPUpload = this.terminateISPUpload.bind(this);
    this.monitorStatus = this.monitorStatus.bind(this);
  }

  render() {
    return null;
  }

  componentDidMount() {
    if (this.props.location.search) {
      const query = querystring.parse(this.props.location.search);
      if (query.ispId && query.ispName && query.ispType) {
        this.ispUpload([
          {
            name: query.ispName,
            type: query.ispType,
            integrationId: query.ispId,
          },
        ]);
        this.props.navigate(this.props.location.pathname);
      }
    }
    this.monitorStatus();
  }

  componentWillUnmount() {
    this.props.removeStatusListener(PROGRESS_TYPE);
  }

  monitorStatus() {
    this.props.viewEnableTermination();
    this.props.addStatusListener(PROGRESS_TYPE, this.displayISPUploadProgress);
  }

  ispUpload(selectedFiles) {
    const selectedIntegrationId = selectedFiles
      ? selectedFiles.length > 0
        ? selectedFiles[0].integrationId
        : ''
      : '';
    const cloudImportRequest = {
      projectId: this.props.projectId,
      integrationId: selectedIntegrationId,
      files: selectedFiles.map(selectedFile => ({
        fileName: selectedFile.name,
        type: selectedFile.type,
      })),
    };

    axios
      .post(URL.PREPROCESSOR_OP(ISP_OP.IMPORT, ISP_SUB_OP.CLOUD), cloudImportRequest)
      .then(res => this.handleClarificationResponse(res.data))
      .catch(error => toast.error(`data upload error: ${error.message}`));
  }

  handleFileImportResponse(res) {
    let response = res.data.body;
    if (response.errors && response.errors.length > 0) {
      this.finishUpload('ADD_DATA');
    } else {
      this.clarifyImport(response);
    }
  }

  clarifyImport(importRequest) {
    axios
      .post(URL.PREPROCESSOR_OP(ISP_OP.IMPORT, ISP_SUB_OP.CLARIFY), importRequest)
      .then(res => this.handleClarificationResponse(res.data))
      .catch(error => toast.error(`data upload error: ${error.message}`));
  }

  handleClarificationResponse(response) {
    if (response.errors && response.errors.length > 0) {
      this.finishUpload(PROGRESS_TYPE);
    } else {
      this.monitorStatus();
    }
  }

  displayISPUploadProgress(status) {
    this.props.setISPActionInProgress('upload');
    if (Util.isRealNumberEqual(status.percentCompleted, 100)) {
      this.props.removeStatusListener(PROGRESS_TYPE);
      if (status.prompts && status.prompts.length > 0) {
        this.showInitialAssistant(status);
      } else {
        this.requestImportFinalization([], status.id);
      }
    }
  }

  showInitialAssistant(taskProgress) {
    if (this.props.assistantPanel.current) {
      this.props.assistantPanel.current.display(
        this.getQuestionsForm(taskProgress.prompts, taskProgress.defaults),
        PROGRESS_TYPE,
        null,
        this.terminateISPUpload
      );
    }
  }

  getQuestionsForm(questions, defaults) {
    return (
      <StageAssistantForm
        prompts={questions}
        defaults={defaults}
        onSubmit={this.requestImportFinalization}
        onCancel={this.terminateISPUpload}
        title="We need a little more information to finish adding this data."
      />
    );
  }

  requestImportFinalization(answers, resourceId) {
    if (this.props.assistantPanel.current) {
      // TODO: validate this since ISP is not part of the stage.
      this.props.assistantPanel.current.hideStage(PROGRESS_TYPE);
    }

    const finalizationRequest = {
      projectId: this.props.projectId,
      resourceId: resourceId,
      answers: answers,
    };

    axios
      .post(URL.PREPROCESSOR_OP(ISP_OP.IMPORT, ISP_SUB_OP.FINALIZE), finalizationRequest)
      .then(res => this.handleFinalizationResponse(res.data))
      .catch(error => toast.error(`isp upload error: ${error.message}`));
  }

  handleFinalizationResponse(response) {
    this.props.viewDisableTermination();
    if (response.prompts && response.prompts.length > 0) {
      this.showInitialAssistant(response);
    } else {
      if (!response.errors?.length) {
        toast.success('Successfully imported Pre Processor');
      }
      this.finishUpload(null);
    }
  }

  async finishUpload(stage) {
    if (this.props.onFinish) {
      this.props.onFinish(stage);
      await mutate(PreProcessorListUrl({projectId: this.props.projectId}));
      await this.props.projectFetch(this.props.projectId);
    }
  }

  terminateISPUpload() {
    this.props.viewDisableTermination();
    return axios
      .get(URL.OPERATION_CANCEL(PROGRESS_TYPE), {
        params: {projectId: this.props.projectId},
      })
      .then(res => {
        this.props.assistantPanel.current.hideStage(PROGRESS_TYPE);
        this.props.removeStatusListener(PROGRESS_TYPE);
        this.finishUpload(PROGRESS_TYPE);
      })
      .catch(error => toast.error(`cancel upload error: ${error.message}`));
  }
}

ISPUploadAction.propTypes = {
  projectId: PropTypes.string.isRequired,
  assistantPanel: PropTypes.shape({current: PropTypes.instanceOf(AssistantPanel)})
    .isRequired,
  onFinish: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  location: getLocation(state),
});

const mapDispatchToProps = {
  addStatusListener: addListener,
  removeStatusListener: removeListener,
  navigate: push,
  projectFetch,
  viewEnableTermination,
  viewDisableTermination,
};

export {ISPUploadAction as ISPUploadActionBase};

export default connect(mapStateToProps, mapDispatchToProps, null, {forwardRef: true})(
  ISPUploadAction
);
